<script setup lang="ts">
import { ref } from "vue";
import {useRequestsStore} from "../../stores/requestStore";
import ConfirmPopup from "primevue/confirmpopup";

const props = defineProps({
  showActions: {
    type: Boolean,
    default: true
  }
});

const requestsStore = useRequestsStore();
import NewRequestModal from "../../components/requests/NewRequestModal.vue";
const NewRequestModalVisible = ref(false);
const completeRequestDialogVisible = ref(false);
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import backendCall from "../../services/AxiosService";
import RequestCompleteDialog from "../../components/requests/RequestCompleteDialog.vue";
import {toDashedTrainId} from "../../utilities/toDashedTrainId";


const formatTemperatureDisplay = (value: unknown, unit?: string | null) => {
  if (value === null || value === undefined) {
    return null;
  }

  const numeric = typeof value === 'number' ? value : parseFloat(String(value));
  if (Number.isNaN(numeric)) {
    return null;
  }

  const normalizedUnit = unit ? unit.toString().toUpperCase() : '';
  return `${numeric}${normalizedUnit ? `°${normalizedUnit}` : ''}`;
};

const requestTemperatureEntries = (request: Record<string, any>) => {
  const entries: Array<{ key: string; label: string; value: string }> = [];

  const tempUnit = request?.unit_check_temp_measurement_unit ?? null;

  const setpoint = formatTemperatureDisplay(
    request?.unit_check_actual_setpoint ?? request?.unit_check_setpoint ?? null,
    tempUnit
  );
  if (setpoint) {
    entries.push({ key: 'setpoint', label: 'SP', value: setpoint });
  }

  const supplyUnit = request?.unit_check_supply_temp_unit ?? tempUnit;
  const supply = formatTemperatureDisplay(request?.unit_check_supply_temp ?? null, supplyUnit);
  if (supply) {
    entries.push({ key: 'supply', label: 'S', value: supply });
  }

  const returnUnit = request?.unit_check_return_temp_unit ?? tempUnit;
  const returnTemp = formatTemperatureDisplay(request?.unit_check_return_temp ?? null, returnUnit);
  if (returnTemp) {
    entries.push({ key: 'return', label: 'R', value: returnTemp });
  }

  return entries;
};


const confirm = useConfirm();
const toast = useToast();


const deleteRequestConfirmation = (requestId , event) => {
  confirm.require({
    target: event.currentTarget,
    message: 'Are you sure you want to remove this request?',
    icon: 'pi pi-info-circle',
    rejectClass: 'p-button-secondary p-button-outlined p-button-sm',
    acceptClass: 'p-button-danger p-button-sm',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    accept: async () => {
      try {
        const deleteRequestCall = await backendCall.delete(`requests/${requestId}`)
        if (deleteRequestCall.error) {
          throw new Error('API Call Error')
        }
        requestsStore.fetchRequests()
        toast.add({severity: 'success', summary: 'Confirmed', detail: `Request ${requestId} Deleted`, life: 3000});
      } catch (e) {
        toast.add({ severity: 'error', summary: 'Failed', detail: `Failed to remove request ${requestId}`, life: 3000 });
      }
      },
    reject: () => {
    }
  });
};

const completeRequestConfirmation = (requestId , event) => {
  confirm.require({
    target: event.currentTarget,
    message: 'Are you sure you want to mark this request as complete?',
    icon: 'pi pi-info-circle',
    rejectClass: 'p-button-secondary p-button-outlined p-button-sm',
    acceptClass: 'p-button-success p-button-sm',
    rejectLabel: 'Cancel',
    acceptLabel: 'Confirm',
    accept: async () => {
      try {
        const completeRequestCall = await backendCall.post(`requests/${requestId}/complete`)
        if (completeRequestCall?.error) {
          throw new Error('API Call Error')
        }
        requestsStore.fetchRequests()
        toast.add({severity: 'success', summary: 'Request Complete', detail: `Request ${requestId} has been marked as complete!`, life: 3000});
      } catch (e) {
        toast.add({ severity: 'error', summary: 'Failed', detail: `Failed to complete request ${requestId}`, life: 3000 });
      }
    },
    reject: () => {
    }
  });
};
</script>

<template>
  <div>
      <DataTable :value="requestsStore.requests.slice(0, 15)" stripedRows size="small">
        <Column field="request_id" header="REQ ID" style="width: 60px"></Column>
        <Column field="equipment_id" header="Unit" style="width: 80px">
          <template #body="slotProps">
            <span class="text-blue-600 dark:text-blue-400 font-medium">{{ slotProps.data.equipment_id }}</span>
          </template>
        </Column>
        <Column field="train_id" header="Train" style="width: 80px">
          <template #body="slotProps">
            {{toDashedTrainId(slotProps.data.train_id)}}
          </template>
        </Column>
        <Column field="request_reason" header="Reason" style="width: 120px">
          <template #body="slotProps">
            <span class="text-slate-700 dark:text-slate-300 font-medium">
              {{ slotProps.data.request_reason || slotProps.data.request_type || '—' }}
            </span>
          </template>
        </Column>
        <Column field="request_billed_setpoint" header="Setpoint" style="width: 80px">
          <template #body="slotProps">
            {{ slotProps.data.request_billed_setpoint }}{{ slotProps.data.request_billed_setpoint_temp_unit ? '°' + slotProps.data.request_billed_setpoint_temp_unit : '' }}
          </template>
        </Column>
        <Column header="Temps" style="width: 110px">
          <template #body="slotProps">
            <div class="flex flex-col leading-tight text-[12px]">
              <template v-if="requestTemperatureEntries(slotProps.data).length">
                <span v-for="entry in requestTemperatureEntries(slotProps.data)" :key="entry.key" class="text-slate-600 dark:text-slate-300">
                  {{ entry.label }}: {{ entry.value }}
                </span>
              </template>
              <span v-else class="text-slate-400 dark:text-slate-500">—</span>
            </div>
          </template>
        </Column>
        <Column field="request_notes" header="Description" style="min-width: 150px; flex-grow: 1">
          <template #body="slotProps">
            <span v-tooltip.top="slotProps.data.request_notes">{{ slotProps.data.request_notes }}</span>
          </template>
        </Column>
        <Column v-if="props.showActions" header="actions" style="width: 70px">
          <template #body="slotProps">
            <div class="flex gap-1 justify-around">
            <Button @click="completeRequestConfirmation(slotProps.data.request_id,$event)" icon="pi pi-check" severity="success" text rounded size="small" aria-label="Submit-Info" class="p-button-sm" />
            <Button @click="deleteRequestConfirmation(slotProps.data.request_id,$event)" icon="pi pi-times" severity="danger" text rounded size="small" aria-label="Cancel" class="p-button-sm" />
            </div>
          </template>
        </Column>
      </DataTable>
      <div v-if="requestsStore.requests.length > 15" class="text-center text-xs text-gray-500 mt-1">
        Showing 15 of {{ requestsStore.requests.length }} requests
      </div>
      <NewRequestModal v-bind:visible="NewRequestModalVisible" @visibleChange="NewRequestModalVisible = false" ></NewRequestModal>
      <RequestCompleteDialog v-if="completeRequestDialogVisible" v-model:visible="completeRequestDialogVisible"></RequestCompleteDialog>
    </div>
  <ConfirmPopup></ConfirmPopup>

</template>

<style scoped>
:deep(.p-datatable) {
  font-size: 0.9rem;
}

:deep(.p-datatable-wrapper) {
  /* Removed max-height and overflow-y to prevent scrolling */
}

:deep(.p-datatable-table) {
  width: 100% !important;
}

/* Make rows more compact to fit all 15 requests without scrolling */
:deep(.p-datatable-tbody > tr > td) {
  padding: 0.25rem 0.5rem;
  height: 2rem;
  max-height: 2rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

:deep(.p-datatable-thead > tr > th) {
  padding: 0.25rem 0.5rem;
  height: 2rem;
}

/* Reduce the height of the buttons in the actions column */
:deep(.p-button.p-button-icon-only) {
  width: 1.5rem;
  height: 1.5rem;
}
</style>
